import React, { useEffect, useMemo, useState } from "react";
import { Search, Download, RefreshCw, AlertTriangle, Hourglass, PackageSearch, CheckCircle2, CalendarClock, TrendingUp, Layers, Archive, X, ChevronRight, ChevronDown, ClipboardPlus } from "lucide-react";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from "recharts";
import { api, formatApiError, downloadFile } from "../lib/api";
import { PageHeader, Panel } from "../lib/ui";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";

const num = (n) => (Number(n) || 0).toLocaleString("id-ID");
const idr = (n) => `Rp${num(n)}`;
const shortIdr = (n) => { n = Number(n) || 0; if (n >= 1e9) return `Rp${(n / 1e9).toFixed(1)}M`; if (n >= 1e6) return `Rp${(n / 1e6).toFixed(1)}jt`; if (n >= 1e3) return `Rp${(n / 1e3).toFixed(0)}rb`; return `Rp${n}`; };

const PRIO = {
  Urgent: { label: "URGENT", cls: "bg-red-50 text-red-600 border-red-200", bar: "#dc2626", tab: "urgent" },
  "Prepare Now": { label: "PREPARE", cls: "bg-amber-50 text-amber-700 border-amber-200", bar: "#d97706", tab: "prepare" },
  Monitor: { label: "MONITOR", cls: "bg-blue-50 text-blue-700 border-blue-200", bar: "#2563eb", tab: "monitor" },
  Safe: { label: "SAFE", cls: "bg-green-50 text-green-700 border-green-200", bar: "#16a34a", tab: "safe" },
  "No Sales": { label: "NO SALES", cls: "bg-slate-100 text-slate-500 border-slate-200", bar: "#94a3b8", tab: "nosales" },
};
const productionKey = (name, color, size) => `${name || ""} ${color || ""} ${size || ""}`.toLowerCase().replace(/\s+/g, " ").trim();
const CHANNELS = ["all", "Shopee", "TikTok", "Tokopedia", "Lazada", "Website", "WhatsApp", "Reseller", "Kasir/Offline"];

function Kpi({ icon: Icon, label, value, sub, color }) {
  return (
    <Panel className="p-4 flex-1 min-w-[170px]">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}><Icon className="w-5 h-5" /></div>
        <div>
          <div className="text-xs text-wmuted">{label}</div>
          <div className="text-2xl font-extrabold text-cocoa leading-tight">{value}</div>
          <div className="text-[11px] text-wmuted">{sub}</div>
        </div>
      </div>
    </Panel>
  );
}

function DaysCoverBar({ dc, priority }) {
  if (dc === null || dc === undefined) return <span className="text-wmuted text-sm">—</span>;
  const bar = PRIO[priority]?.bar || "#94a3b8";
  const pct = Math.max(6, Math.min(100, (dc / 30) * 100));
  return (
    <div>
      <div className="text-sm font-medium" style={{ color: bar }}>{dc === 0 ? "0 hari" : `${dc} hari`}</div>
      <div className="h-1.5 rounded-full bg-slate-100 mt-1 w-24"><div className="h-full rounded-full" style={{ width: `${pct}%`, background: bar }} /></div>
    </div>
  );
}

export default function RestockPlanning() {
  const { canEdit } = useAuth();
  const [period, setPeriod] = useState(30);
  const [warehouse, setWarehouse] = useState("all");
  const [channel, setChannel] = useState("all");
  const [lead, setLead] = useState(21);
  const [buffer, setBuffer] = useState(7);
  const [group, setGroup] = useState("product");
  const [warehouses, setWarehouses] = useState([]);
  const [data, setData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [best, setBest] = useState([]);
  const [bestShown, setBestShown] = useState(30);
  const [productionByVariant, setProductionByVariant] = useState({});
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [selected, setSelected] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [bdLoading, setBdLoading] = useState(false);
  const [stuckData, setStuckData] = useState(null);
  const [stuckLoading, setStuckLoading] = useState(false);
  const [requestData, setRequestData] = useState(null);
  const [requestQty, setRequestQty] = useState(0);
  const [requestPreview, setRequestPreview] = useState(null);
  const [requestLoading, setRequestLoading] = useState(false);
  const canRequestMaterial = canEdit("stock_prediction");

  useEffect(() => {
    if (tab !== "stuck") return;
    setStuckLoading(true);
    api.get("/restock/stuck", { params: { period, warehouse, channel } })
      .then((r) => setStuckData(r.data)).catch(() => setStuckData({ products: [] }))
      .finally(() => setStuckLoading(false));
  }, [tab, period, warehouse, channel]);

  useEffect(() => { api.get("/warehouses").then((r) => setWarehouses(r.data || [])).catch(() => {}); }, []);

  const analysisParams = useMemo(() => ({ period, warehouse, channel, lead_time: lead, buffer, group }), [period, warehouse, channel, lead, buffer, group]);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/restock/analysis", { params: analysisParams }),
      api.get("/restock/trends", { params: { period, warehouse, channel } }),
      api.get("/restock/bestsellers", { params: { period, warehouse, channel, lead_time: lead, buffer, limit: 100 } }),
      api.get("/production/orders"),
      api.get("/production/cutting"),
    ]).then(([a, t, b, orders, cuttings]) => { setData(a.data); setTrends(t.data); setBest(b.data.rows || []); setBestShown(30); setSelected(null); const running = {}; const add = (keys, info) => keys.filter(Boolean).forEach((key) => { const old = running[key] || { ...info, qty: 0 }; running[key] = { ...old, qty: old.qty + info.qty, target_date: !old.target_date || (info.target_date && info.target_date < old.target_date) ? info.target_date : old.target_date }; }); (orders.data || []).filter((o) => ["Siap Dikirim", "Dikirim ke Konveksi", "Sedang Dikerjakan", "Diterima Sebagian", "In Production"].includes(o.status)).forEach((o) => (o.details || []).forEach((d) => add([d.variant_id, productionKey(d.product_name || o.product_name, d.color, d.size)], { qty: Math.max(0, Number(d.qty || 0) - Number(d.received_qty || 0)), target_date: o.target_date, status: o.status, number: o.number, stage: "Konveksi" }))); (cuttings.data || []).filter((c) => !["Selesai", "Dibatalkan"].includes(c.status)).forEach((c) => (c.targets || []).forEach((d) => add([productionKey(c.product_name, c.color, d.size)], { qty: Math.max(0, Number(d.qty || 0) - Number(c.output_total || 0)), target_date: c.target_date, status: c.status, number: c.number, stage: "Cutting" }))); setProductionByVariant(running); })
      .catch(() => toast.error("Gagal memuat analisis"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [analysisParams]);

  const categories = useMemo(() => {
    if (!data) return [];
    return Array.from(new Set(data.rows.map((r) => r.category).filter(Boolean))).sort();
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    let rows = data.rows;
    if (tab !== "all") rows = rows.filter((r) => PRIO[r.priority]?.tab === tab);
    if (cat !== "all") rows = rows.filter((r) => r.category === cat);
    if (q) { const ql = q.toLowerCase(); rows = rows.filter((r) => r.name.toLowerCase().includes(ql)); }
    return rows;
  }, [data, tab, cat, q]);

  const doExport = () => downloadFile("/restock/export", { ...analysisParams, tab }, "restock_planning.xlsx").catch(() => toast.error("Gagal export"));
  const openBreakdown = (row) => {
    setBreakdown({ product: row, rows: null });
    setBdLoading(true);
    api.get(`/restock/product/${row.product_id}/variants`, { params: analysisParams })
      .then((r) => setBreakdown({ product: row, rows: r.data.rows }))
      .catch(() => toast.error("Gagal memuat varian"))
      .finally(() => setBdLoading(false));
  };
  const openMaterialRequest = (row) => {
    setRequestData(row);
    setRequestQty(Number(row.suggested_restock) || 0);
    setRequestPreview(null);
    setRequestLoading(true);
    api.get(`/restock/material-preview/${row.id}`)
      .then((response) => setRequestPreview(response.data))
      .catch((error) => toast.error(formatApiError(error.response?.data?.detail)))
      .finally(() => setRequestLoading(false));
  };
  const createMaterialRequest = async () => {
    const requestedQty = Number(requestQty);
    if (!requestData || requestedQty <= 0) {
      toast.error("Qty request harus lebih dari 0");
      return;
    }
    if (!Number.isInteger(requestedQty)) {
      toast.error("Qty request produk harus dalam satuan pcs bulat");
      return;
    }
    try {
      const response = await api.post("/restock/proposals", {
        variant_id: requestData.id,
        qty: requestedQty,
        period,
        warehouse,
        channel,
        lead_time: lead,
        buffer,
      });
      toast.success(`Usulan Restock ${response.data.number} dibuat`);
      setRequestData(null);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };
  const kpi = data?.kpi;
  const tc = data?.tab_counts || {};
  const asOf = data ? new Date((data.as_of || "").replace(" ", "T")).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }) : "";

  return (
    <div data-testid="restock-planning-page">
      <PageHeader title="Restock Planning" subtitle="Prioritaskan restock dari sales velocity, sisa stok & lead time">
        <Button variant="outline" onClick={doExport} data-testid="restock-export-btn"><Download className="w-4 h-4 mr-1.5" />Export</Button>
        <Button onClick={load} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="restock-refresh-btn"><RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />Refresh Analysis</Button>
      </PageHeader>

      {/* Filter bar */}
      <Panel className="p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-xs">Periode Sales</Label>
            <Select value={String(period)} onValueChange={(v) => setPeriod(Number(v))}>
              <SelectTrigger className="h-9 w-36" data-testid="restock-period"><SelectValue /></SelectTrigger>
              <SelectContent>{[30, 60, 90, 180].map((n) => <SelectItem key={n} value={String(n)}>{n} Hari Terakhir</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Gudang</Label>
            <Select value={warehouse} onValueChange={setWarehouse}>
              <SelectTrigger className="h-9 w-44" data-testid="restock-warehouse"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Semua Gudang</SelectItem>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Channel</Label>
            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger className="h-9 w-40" data-testid="restock-channel"><SelectValue /></SelectTrigger>
              <SelectContent>{CHANNELS.map((c) => <SelectItem key={c} value={c}>{c === "all" ? "Semua Channel" : c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Lead Time (hari)</Label><Input type="number" min={0} value={lead} onChange={(e) => setLead(Number(e.target.value))} className="h-9 w-28" data-testid="restock-lead" /></div>
          <div><Label className="text-xs">Buffer (hari)</Label><Input type="number" min={0} value={buffer} onChange={(e) => setBuffer(Number(e.target.value))} className="h-9 w-28" data-testid="restock-buffer" /></div>
          <div className="text-xs text-wmuted ml-auto flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" />Data s/d {asOf}</div>
        </div>
      </Panel>

      {/* KPIs */}
      <div className="flex flex-wrap gap-3 mb-4">
        <Kpi icon={AlertTriangle} label="Urgent SKUs" value={num(kpi?.urgent_skus)} sub="Stockout sebelum lead time" color="bg-red-50 text-red-600" />
        <Kpi icon={Hourglass} label="Prepare Now" value={num(kpi?.prepare_now)} sub="Stockout dalam lead time" color="bg-amber-50 text-amber-600" />
        <Kpi icon={PackageSearch} label="Recommended Restock" value={`${num(kpi?.recommended_restock)} pcs`} sub="Semua prioritas" color="bg-cocoa/10 text-cocoa" />
        <Kpi icon={Archive} label="No Sales SKUs" value={num(kpi?.no_sales)} sub="Tanpa penjualan (stok mati)" color="bg-slate-100 text-slate-500" />
        <Kpi icon={CheckCircle2} label="Data Match" value={`${kpi?.data_match ?? 0}%`} sub={`${num(kpi?.sku_matched)}/${num(kpi?.sku_total)} SKU cocok`} color="bg-green-50 text-green-600" />
        <Kpi icon={CalendarClock} label="Ready Date" value={kpi?.ready_date ? new Date(kpi.ready_date).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" }) : "-"} sub="Target siap" color="bg-blue-50 text-blue-600" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* Main table */}
        <div className="xl:col-span-2 space-y-4">
          <Panel className="p-0">
            {/* Tabs */}
            <div className="flex items-center gap-1 px-4 pt-3 border-b border-taupe/15 overflow-x-auto">
              {[["all", "All", tc.all], ["urgent", "Urgent", tc.urgent], ["prepare", "Prepare Now", tc.prepare], ["monitor", "Monitor", tc.monitor], ["safe", "Safe", tc.safe], ["nosales", "No Sales", tc.no_sales], ["stuck", "Product Stuck", stuckData?.products?.length]].map(([k, lbl, n]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`restock-tab-${k}`}
                  className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === k ? "border-cocoa text-cocoa" : "border-transparent text-wmuted hover:text-cocoa"}`}>
                  {lbl} <span className="text-xs text-wmuted">{n ?? 0}</span>
                </button>
              ))}
            </div>
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3 px-4 py-3">
              <div className="relative w-52"><Search className="absolute left-2.5 top-2.5 w-4 h-4 text-wmuted" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk..." className="pl-8 h-9" data-testid="restock-search" />
              </div>
              <Select value={cat} onValueChange={setCat}>
                <SelectTrigger className="h-9 w-40" data-testid="restock-category"><SelectValue placeholder="Kategori" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Semua Kategori</SelectItem>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <div className="ml-auto flex rounded-lg border border-taupe/25 overflow-hidden">
                <button onClick={() => setGroup("product")} data-testid="restock-by-product" className={`px-3 py-1.5 text-sm ${group === "product" ? "bg-cocoa text-ivory" : "text-cocoa"}`}>By Product</button>
                <button onClick={() => setGroup("variant")} data-testid="restock-by-variant" className={`px-3 py-1.5 text-sm ${group === "variant" ? "bg-cocoa text-ivory" : "text-cocoa"}`}>By Variant</button>
              </div>
            </div>

            {tab === "stuck" ? <StuckTable data={stuckData} loading={stuckLoading} /> : <div className="max-h-[560px] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-ivory z-10">
                  <TableRow>
                    <TableHead>Produk</TableHead>
                    <TableHead className="text-center">Available</TableHead>
                    <TableHead className="text-center">Avg/Day</TableHead>
                    <TableHead>Days Cover</TableHead>
                    <TableHead>Stockout</TableHead>
                    <TableHead className="text-center">Suggested</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-10 text-wmuted">Menganalisis...</TableCell></TableRow>
                  ) : filtered.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-10 text-wmuted">Tidak ada data</TableCell></TableRow>
                  ) : filtered.slice(0, 200).map((r) => (
                    <TableRow key={r.id} onClick={() => setSelected(r)} data-testid={`restock-row-${r.id}`}
                      className={`cursor-pointer ${selected?.id === r.id ? "bg-cocoa/5" : ""}`}>
                      <TableCell>
                        <div className="font-medium text-cocoa text-sm">{r.name}</div>
                        {group === "product" && r.critical_variants > 0 && <div className="text-[11px] text-red-500">{r.critical_variants} varian kritis</div>}
                        {group === "product" && !r.critical_variants && <div className="text-[11px] text-wmuted">{r.total_variants} varian</div>}
                      </TableCell>
                      <TableCell className="text-center font-medium">{num(r.available)}</TableCell>
                      <TableCell className="text-center">{r.avg_sales_day}</TableCell>
                      <TableCell><DaysCoverBar dc={r.days_cover} priority={r.priority} /></TableCell>
                      <TableCell className="text-sm">{r.stockout_date ? (r.days_cover === 0 ? "Hari ini" : new Date(r.stockout_date).toLocaleDateString("id-ID", { day: "numeric", month: "short" })) : "—"}</TableCell>
                      <TableCell className="text-center font-semibold text-cocoa">{num(r.suggested_restock)}</TableCell>
                      <TableCell><span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold border ${PRIO[r.priority]?.cls}`}>{PRIO[r.priority]?.label}</span></TableCell>
                      <TableCell className="text-right">
                        {group === "variant" && canRequestMaterial && (r.priority === "Urgent" || r.priority === "Prepare Now") && <Button size="sm" variant="outline" onClick={(event) => { event.stopPropagation(); openMaterialRequest(r); }} data-testid={`restock-request-${r.id}`} className="border-cocoa text-cocoa"><ClipboardPlus className="w-3.5 h-3.5 mr-1" />Request</Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>}
          </Panel>

          {/* Trends */}
          <Panel className="p-4">
            <div className="flex items-center gap-2 mb-3"><TrendingUp className="w-4 h-4 text-cocoa" /><h3 className="font-semibold text-cocoa">Tren Penjualan ({period} hari)</h3></div>
            <div className="h-56" data-testid="restock-trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trends?.series || []} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <defs><linearGradient id="gq" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#7c5a3a" stopOpacity={0.35} /><stop offset="95%" stopColor="#7c5a3a" stopOpacity={0} /></linearGradient></defs>
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => (d || "").slice(5)} minTickGap={20} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v, n) => n === "revenue" ? idr(v) : num(v)} labelFormatter={(d) => `Tgl ${d}`} />
                  <Area type="monotone" dataKey="qty" name="Qty Terjual" stroke="#7c5a3a" strokeWidth={2} fill="url(#gq)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          {/* Best Sellers — jaga stok jangan habis */}
          <Panel className="p-0" data-testid="restock-bestsellers-panel">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-taupe/15">
              <AlertTriangle className="w-4 h-4 text-cocoa" />
              <h3 className="font-semibold text-cocoa">Top {Math.min(bestShown, best.length) || 30} Best Seller (per varian)</h3>
              <span className="text-xs text-wmuted ml-auto">Jaga stok best seller jangan sampai habis</span>
            </div>
            <div className="max-h-[520px] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-ivory z-10">
                  <TableRow>
                    <TableHead className="w-10">#</TableHead>
                    <TableHead>Varian</TableHead>
                    <TableHead className="text-center">Terjual ({period}h)</TableHead>
                    <TableHead className="text-center">Available</TableHead>
                    <TableHead>Days Cover</TableHead>
                    <TableHead className="text-center">Suggested</TableHead>
                    <TableHead>Kain</TableHead>
                    <TableHead>Produksi</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {best.length === 0 ? (
                    <TableRow><TableCell colSpan={10} className="text-center py-8 text-wmuted">{loading ? "Memuat..." : "Belum ada data"}</TableCell></TableRow>
                  ) : best.slice(0, bestShown).map((r, i) => {
                    const risk = r.priority === "Urgent" || r.priority === "Prepare Now";
                    const production = productionByVariant[r.id] || productionByVariant[productionKey(r.product_name || r.name, r.color, r.size)] || productionByVariant[productionKey(r.name)];
                    return (
                      <TableRow key={r.id} data-testid={`bestseller-row-${r.id}`} className={risk ? "bg-red-50/40" : ""}>
                        <TableCell className="text-wmuted font-semibold">{i + 1}</TableCell>
                        <TableCell>
                          <div className="font-medium text-cocoa text-sm">{r.name}</div>
                          {risk && <div className="text-[11px] text-red-500 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Rawan habis</div>}
                        </TableCell>
                        <TableCell className="text-center font-bold text-cocoa">{num(r.period_qty)}</TableCell>
                        <TableCell className="text-center font-medium">{num(r.available)}</TableCell>
                        <TableCell><DaysCoverBar dc={r.days_cover} priority={r.priority} /></TableCell>
                        <TableCell className="text-center font-semibold text-cocoa">{num(r.suggested_restock)}</TableCell>
                        <TableCell data-testid={`bestseller-kain-${r.id}`}>
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${r.fabric_status === "KAIN ADA" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}
                            title={r.has_bom ? `Kain tersedia: ${num(r.fabric_available)}` : "Belum ada BOM kain"}>
                            {r.fabric_status || "-"}
                          </span>
                        </TableCell>
                        <TableCell data-testid={`bestseller-production-${r.id}`}><ProductionBadge production={production} /></TableCell>
                        <TableCell><span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold border ${PRIO[r.priority]?.cls}`}>{PRIO[r.priority]?.label}</span></TableCell>
                        <TableCell className="text-right">
                          {canRequestMaterial && risk && <Button size="sm" variant="outline" onClick={() => openMaterialRequest(r)} data-testid={`bestseller-request-${r.id}`} className="border-cocoa text-cocoa"><ClipboardPlus className="w-3.5 h-3.5 mr-1" />Request</Button>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            {best.length > bestShown && (
              <div className="flex items-center justify-center gap-3 px-4 py-3 border-t border-taupe/15">
                <span className="text-xs text-wmuted">Menampilkan {Math.min(bestShown, best.length)} dari {best.length}</span>
                <Button size="sm" variant="outline" className="border-cocoa text-cocoa" data-testid="bestseller-show-more" onClick={() => setBestShown((n) => (n < 60 ? 60 : 100))}>
                  Show More (+{Math.min((bestShown < 60 ? 60 : 100), best.length) - bestShown})
                </Button>
              </div>
            )}
          </Panel>
        </div>

        {/* Right column */}
        <div className="space-y-4">
          {/* Why recommended */}
          <Panel className="p-4" data-testid="restock-detail-panel">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-cocoa">Kenapa direkomendasikan</h3>
              {selected && <button onClick={() => setSelected(null)}><X className="w-4 h-4 text-wmuted" /></button>}
            </div>
            {!selected ? (
              <div className="text-sm text-wmuted py-8 text-center">Pilih produk di tabel untuk melihat rincian & formula restock.</div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-cocoa">{selected.name}</div>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold border ${PRIO[selected.priority]?.cls}`}>{PRIO[selected.priority]?.label}</span>
                </div>
                {[["Available Stock", `${num(selected.available)} pcs`], ["Incoming Stock", `${num(selected.incoming)} pcs`], ["Sales Velocity", `${selected.avg_sales_day} pcs/hari`], ["Lead Time + Buffer", `${lead + buffer} hari`], ["Coverage", `${data?.params?.coverage} hari`], ["Safety Factor", data?.params?.safety]].map(([k, v]) => (
                  <div key={k} className="flex justify-between text-sm border-b border-taupe/10 pb-1.5"><span className="text-wmuted">{k}</span><span className="font-medium text-cocoa">{v}</span></div>
                ))}
                <div className="flex justify-between items-center pt-1">
                  <span className="font-semibold text-cocoa">Suggested Restock</span>
                  <span className="text-2xl font-extrabold text-cocoa">{num(selected.suggested_restock)} pcs</span>
                </div>
                <div className="bg-taupe/5 rounded-lg p-3 text-xs text-wmuted">
                  <div className="font-semibold text-cocoa mb-1">Formula</div>
                  avg/hari × (lead + buffer + coverage) × safety − available − incoming
                </div>
                {group === "product" && (
                  <Button variant="outline" className="w-full border-cocoa/30 text-cocoa" onClick={() => openBreakdown(selected)} data-testid="restock-variant-breakdown-btn">
                    <Layers className="w-4 h-4 mr-1.5" />View Variant Breakdown
                  </Button>
                )}
              </div>
            )}
          </Panel>

          {/* Channel insight */}
          <Panel className="p-4" data-testid="restock-channel-panel">
            <h3 className="font-semibold text-cocoa mb-3">Performa Channel ({period} hari)</h3>
            <div className="h-40 mb-2">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={(trends?.channels || []).slice(0, 6)} layout="vertical" margin={{ left: 10, right: 10 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="channel" tick={{ fontSize: 11 }} width={90} />
                  <Tooltip formatter={(v) => idr(v)} />
                  <Bar dataKey="revenue" radius={[0, 4, 4, 0]}>
                    {(trends?.channels || []).slice(0, 6).map((c, i) => <Cell key={i} fill={["#7c5a3a", "#a67c52", "#c9a882", "#8b9dc3", "#b0b0b0", "#d0c0a8"][i % 6]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-1.5">
              {(trends?.channels || []).map((c) => (
                <div key={c.channel} className="flex justify-between text-sm" data-testid={`restock-channel-${c.channel}`}>
                  <span className="text-cocoa">{c.channel}</span>
                  <span className="text-wmuted">{num(c.qty)} pcs · <b className="text-cocoa">{shortIdr(c.revenue)}</b></span>
                </div>
              ))}
            </div>
          </Panel>

          {/* Top products */}
          <Panel className="p-4" data-testid="restock-top-panel">
            <h3 className="font-semibold text-cocoa mb-3">Produk Terlaris</h3>
            <div className="space-y-1.5">
              {(trends?.top_products || []).map((t, i) => (
                <div key={t.product_id || i} className="flex justify-between text-sm">
                  <span className="text-cocoa truncate mr-2">{i + 1}. {t.name}</span>
                  <span className="text-wmuted whitespace-nowrap">{num(t.qty)} pcs</span>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>
      {breakdown && <VariantBreakdownDialog data={breakdown} loading={bdLoading} onClose={() => setBreakdown(null)} />}
      {requestData && <MaterialRequestDialog data={requestData} loading={requestLoading} preview={requestPreview} qty={requestQty} setQty={setRequestQty} onClose={() => setRequestData(null)} onSubmit={createMaterialRequest} />}
    </div>
  );
}

function ProductionBadge({ production }) {
  if (!production) return <span className="text-xs text-wmuted">Belum produksi</span>;
  const estimate = production.target_date ? new Date(production.target_date).toLocaleDateString("id-ID", { day: "numeric", month: "short" }) : "Estimasi belum ada";
  return <div className="min-w-[142px]"><div className="text-[10px] font-bold text-status-passed">{production.stage === "Cutting" ? `CUTTING · ${production.status}` : "SEDANG PRODUKSI"}</div><div className="text-xs text-cocoa">{num(production.qty)} pcs · {production.number}</div><div className="text-[10px] text-wmuted">Est. {estimate}</div></div>;
}

function MaterialRequestDialog({ data, loading, preview, qty, setQty, onClose, onSubmit }) {
  const materials = preview?.materials || [];
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="restock-material-request-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-cocoa">Ajukan Usulan Restock</DialogTitle>
          <DialogDescription>
            Purchase Request akan dibuat sebagai Draft untuk dilengkapi tim purchasing.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="border-b border-taupe/15 pb-3">
            <div className="font-semibold text-cocoa" data-testid="restock-request-product-name">
              {preview?.product_name || data.name}
            </div>
            <div className="text-sm text-wmuted">
              {preview ? `${preview.color || "-"} / ${preview.size || "-"}` : data.priority}
              {" · "}
              {data.priority}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Suggested</Label>
              <div
                className="h-10 flex items-center font-semibold text-cocoa"
                data-testid="restock-request-suggested"
              >
                {num(data.suggested_restock)} pcs
              </div>
            </div>
            <div>
              <Label htmlFor="restock-request-qty">Qty Request Produk</Label>
              <Input
                id="restock-request-qty"
                min="1"
                onChange={(event) => setQty(event.target.value)}
                step="1"
                type="number"
                value={qty}
                data-testid="restock-request-qty"
              />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Material Terkait</Label>
              {!loading && <span
                className={`text-xs font-medium ${
                  preview?.bom_found ? "text-status-passed" : "text-amber-700"
                }`}
                data-testid="restock-request-bom-status"
              >
                {preview?.bom_found ? "BOM terhubung" : "BOM belum dimapping"}
              </span>}
            </div>
            {loading ? (
              <div className="py-6 text-sm text-wmuted" data-testid="restock-request-loading">
                Mengecek BOM dan stok material...
              </div>
            ) : !preview?.bom_found ? (
              <div className="border border-dashed border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                Produk belum memiliki BOM. Draft PR tetap dibuat agar tim purchasing dapat
                menambahkan material yang tepat.
              </div>
            ) : !materials.length ? (
              <div className="border border-dashed border-taupe/30 p-3 text-sm text-wmuted">
                BOM ada, tetapi belum memiliki material kain atau aksesori yang dapat dicek.
              </div>
            ) : (
              <div className="border border-taupe/20 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead>Material</TableHead>
                      <TableHead>Satuan</TableHead>
                      <TableHead className="text-right">Stok Tersedia</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {materials.map((material) => (
                      <TableRow
                        key={`${material.material_kind}-${material.material_id}`}
                        data-testid={`restock-request-material-${material.material_id}`}
                      >
                        <TableCell>{material.name || "-"}</TableCell>
                        <TableCell>{material.unit}</TableCell>
                        <TableCell className="text-right">
                          {num(material.available_qty)}
                        </TableCell>
                        <TableCell>
                          <span className={
                            material.availability === "Tersedia"
                              ? "text-status-passed"
                              : "text-status-reject"
                          }>
                            {material.availability}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="restock-request-cancel">
            Batal
          </Button>
          <Button
            className="bg-cocoa text-ivory"
            disabled={loading || Number(qty) <= 0}
            onClick={onSubmit}
            data-testid="restock-request-submit"
          >
            <ClipboardPlus className="w-4 h-4 mr-1.5" />
            Ajukan Usulan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VariantBreakdownDialog({ data, loading, onClose }) {
  const rows = data.rows || [];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="restock-breakdown-dialog">
        <DialogHeader>
          <DialogTitle>Variant Breakdown — {data.product?.product_name}</DialogTitle>
          <DialogDescription>Rincian per varian: stok, kecepatan jual, days cover & saran restock.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-10 text-center text-wmuted">Memuat varian...</div>
        ) : (
          <div className="border border-taupe/20 rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Varian</TableHead>
                  <TableHead className="text-center">Available</TableHead>
                  <TableHead className="text-center">Avg/Day</TableHead>
                  <TableHead>Days Cover</TableHead>
                  <TableHead>Stockout</TableHead>
                  <TableHead className="text-center">Suggested</TableHead>
                  <TableHead>Priority</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-wmuted">Tidak ada varian</TableCell></TableRow>
                ) : rows.map((r) => (
                  <TableRow key={r.id} data-testid={`restock-bd-row-${r.id}`}>
                    <TableCell className="text-sm">{r.color} {r.size}</TableCell>
                    <TableCell className="text-center font-medium">{num(r.available)}</TableCell>
                    <TableCell className="text-center">{r.avg_sales_day}</TableCell>
                    <TableCell><DaysCoverBar dc={r.days_cover} priority={r.priority} /></TableCell>
                    <TableCell className="text-sm">{r.stockout_date ? (r.days_cover === 0 ? "Hari ini" : new Date(r.stockout_date).toLocaleDateString("id-ID", { day: "numeric", month: "short" })) : "—"}</TableCell>
                    <TableCell className="text-center font-semibold text-cocoa">{num(r.suggested_restock)}</TableCell>
                    <TableCell><span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold border ${PRIO[r.priority]?.cls}`}>{PRIO[r.priority]?.label}</span></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function StuckTable({ data, loading }) {
  const [open, setOpen] = React.useState({});
  if (loading) return <div className="py-12 text-center text-wmuted" data-testid="stuck-loading">Menganalisis produk stuck...</div>;
  const products = data?.products || [];
  if (!products.length) return <div className="py-12 text-center text-wmuted" data-testid="stuck-empty">Tidak ada produk stuck sesuai kriteria (stok ≥ 8, masuk terakhir ≥ 60 hari, avg &lt; 0,5/hari, days cover ≥ 45).</div>;
  return (
    <div className="max-h-[560px] overflow-y-auto" data-testid="stuck-table">
      <Table>
        <TableHeader className="sticky top-0 bg-ivory z-10"><TableRow>
          <TableHead className="w-8"></TableHead><TableHead className="w-8">#</TableHead>
          <TableHead>Produk</TableHead>
          <TableHead className="text-center">Stok Stuck</TableHead>
          <TableHead className="text-center">Varian</TableHead>
          <TableHead className="text-right">Nilai Tertahan</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {products.map((p, i) => (
            <React.Fragment key={p.product_id}>
              <TableRow className="cursor-pointer" onClick={() => setOpen((o) => ({ ...o, [p.product_id]: !o[p.product_id] }))} data-testid={`stuck-row-${p.product_id}`}>
                <TableCell>{open[p.product_id] ? <ChevronDown className="w-4 h-4 text-taupe" /> : <ChevronRight className="w-4 h-4 text-taupe" />}</TableCell>
                <TableCell className="text-wmuted">{i + 1}</TableCell>
                <TableCell className="font-medium text-cocoa text-sm">{p.name}</TableCell>
                <TableCell className="text-center font-semibold">{num(p.stuck_qty)}</TableCell>
                <TableCell className="text-center text-sm text-wmuted">{p.variants.length}</TableCell>
                <TableCell className="text-right font-bold text-cocoa">{idr(p.stuck_value)}</TableCell>
              </TableRow>
              {open[p.product_id] && (
                <TableRow className="bg-taupe/5 hover:bg-taupe/5"><TableCell colSpan={6} className="p-0">
                  <Table><TableHeader><TableRow>
                    <TableHead className="text-[11px]">Warna/Ukuran</TableHead>
                    <TableHead className="text-[11px] text-center">Stok</TableHead>
                    <TableHead className="text-[11px] text-center">Jual 30h</TableHead>
                    <TableHead className="text-[11px] text-center">Avg/hari</TableHead>
                    <TableHead className="text-[11px]">Masuk Terakhir</TableHead>
                    <TableHead className="text-[11px] text-center">Umur (hari)</TableHead>
                    <TableHead className="text-[11px] text-center">Days Cover</TableHead>
                  </TableRow></TableHeader><TableBody>
                    {p.variants.map((v) => (
                      <TableRow key={v.variant_id} data-testid={`stuck-var-${v.variant_id}`}>
                        <TableCell className="text-sm text-cocoa">{v.color}/{v.size}</TableCell>
                        <TableCell className="text-center">{num(v.available)}</TableCell>
                        <TableCell className="text-center">{num(v.period_qty)}</TableCell>
                        <TableCell className="text-center">{v.avg_sales_day}</TableCell>
                        <TableCell className="text-sm">{v.last_incoming ? new Date(v.last_incoming).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "2-digit" }) : "—"}</TableCell>
                        <TableCell className="text-center">{v.age_days >= 9999 ? "—" : v.age_days}</TableCell>
                        <TableCell className="text-center">{v.days_cover == null ? "∞" : v.days_cover}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody></Table>
                </TableCell></TableRow>
              )}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
